import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  LlmLogsResponse,
  TokenAnalysisItem,
  LlmFilters,
  AggregatedLlmStats,
  ProviderCostDto,
} from '../models/llm.types';
import { tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LlmService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  public errorMessage = signal<string | null>(null);

  public llmLogs = signal<LlmLogsResponse>({ data: [], total: 0, page: 0, pageSize: 20 });
  public tokenAnalysis = signal<TokenAnalysisItem[]>([]);
  public aggregatedStats = signal<AggregatedLlmStats | null>(null);
  public providerCosts = signal<ProviderCostDto | null>(null);

  loadLlmLogs(startDate: Date, endDate: Date, page = 0, pageSize = 20, filters?: LlmFilters) {
    let params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (filters?.provider) params = params.set('provider', filters.provider);
    if (filters?.model) params = params.set('model', filters.model);
    if (filters?.search) params = params.set('search', filters.search);

    return this.http.get<LlmLogsResponse>(`${this.apiUrl}/backoffice/llm/logs`, { params }).pipe(
      tap({
        next: (data) => this.llmLogs.set(data),
        error: (err) => this.errorMessage.set(err.message),
      }),
    );
  }

  loadTokenAnalysis(startDate: Date, endDate: Date) {
    this.aggregatedStats.set(null);
    this.providerCosts.set(null);
    this.errorMessage.set(null);

    const params = new HttpParams()
      .set('startDate', startDate.toISOString())
      .set('endDate', endDate.toISOString());

    return this.http
      .get<{
        analysis: TokenAnalysisItem[];
        stats: AggregatedLlmStats;
        providerCosts: ProviderCostDto;
      }>(`${this.apiUrl}/backoffice/llm/analysis`, { params })
      .pipe(
        tap({
          next: (data) => {
            this.tokenAnalysis.set(data.analysis);
            this.aggregatedStats.set(data.stats);
            this.providerCosts.set(data.providerCosts);
          },
          error: (err) => this.errorMessage.set(err.message),
        }),
      );
  }
}
